import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useInvoices, useUpdatePayment } from '@/api/hooks';
import { InvoiceCard } from '@/components/InvoiceCard';
import { MarkPaidDialog } from '@/components/MarkPaidDialog';
import { Button } from '@/components/ui/button';
import { PageContainer } from '@/components/ui/page-container';
import type { PaymentStatus, Invoice } from '@/api/types';
import { toast } from 'sonner';

type FilterTab = 'all' | 'unpaid' | 'paid';

export function InvoiceListView() {
  const { t } = useTranslation();
  const { getToken } = useKindeAuth();
  const getAccessToken = getToken as () => Promise<string>;
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [dialogInvoice, setDialogInvoice] = useState<{ id: string; number: string } | null>(null);

  const statusFilter: PaymentStatus | undefined = activeTab === 'all' ? undefined : activeTab;
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInvoices(statusFilter);
  const updatePayment = useUpdatePayment();

  const invoices: Invoice[] = data?.pages.flatMap((page) => page.invoices) ?? [];

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: t('invoice.all') },
    { key: 'unpaid', label: t('invoice.unpaid') },
    { key: 'paid', label: t('invoice.paid') },
  ];

  function handleMarkPaid(invoiceId: string) {
    const inv = invoices.find((i) => i.id === invoiceId);
    setDialogInvoice(inv ? { id: inv.id, number: inv.invoice_number } : null);
  }

  function handleMarkUnpaid(invoiceId: string) {
    updatePayment.mutate(
      { invoiceId, paymentStatus: 'unpaid' },
      {
        onSuccess: () => toast.success(t('invoice.markedUnpaid')),
        onError: () => toast.error(t('invoice.paymentFailed')),
      },
    );
  }

  function handleConfirmPayment(invoiceId: string, paymentMethod: string, paymentNotes: string) {
    updatePayment.mutate(
      { invoiceId, paymentStatus: 'paid', paymentMethod, paymentNotes },
      {
        onSuccess: () => toast.success(t('invoice.markedPaid')),
        onError: () => toast.error(t('invoice.paymentFailed')),
      },
    );
    setDialogInvoice(null);
  }

  function getEmptyMessage(): string {
    if (activeTab === 'unpaid') return t('invoice.noUnpaidInvoices');
    if (activeTab === 'paid') return t('invoice.noPaidInvoices');
    return t('invoice.noInvoices');
  }

  return (
    <PageContainer>
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">{t('invoice.title')}</h2>

        <div className="flex gap-2">
          {tabs.map((tab) => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </Button>
          ))}
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>{getEmptyMessage()}</p>
            {activeTab === 'all' && <p className="text-sm mt-1">{t('invoice.noInvoicesDesc')}</p>}
          </div>
        ) : (
          <div className="grid gap-4">
            {invoices.map((invoice) => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onMarkPaid={handleMarkPaid}
                onMarkUnpaid={handleMarkUnpaid}
                getAccessToken={getAccessToken}
              />
            ))}
          </div>
        )}

        {hasNextPage && (
          <div className="text-center">
            <Button variant="outline" onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
              {t('invoice.loadMore')}
            </Button>
          </div>
        )}

        <MarkPaidDialog
          open={dialogInvoice !== null}
          invoiceId={dialogInvoice?.id ?? null}
          invoiceNumber={dialogInvoice?.number}
          onConfirm={handleConfirmPayment}
          onCancel={() => setDialogInvoice(null)}
        />
      </div>
    </PageContainer>
  );
}
